import React, { useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import { DateTime } from "components/common/Date";
import OverviewTab from "./OverviewTab";
import LinesTab from "./LinesTab";
import { useGoodsReceiptOverview } from "./GoodsReceiptOverviewContext";
import {
  GoodsReceiptOverviewTabParam,
  grnOverviewActiveTabId,
  isValidGrnOverviewTabParam,
  searchParamForGrnOverviewTabId,
} from "./overviewTabSearchParams";

const Section = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { goodsReceipt: record } = useGoodsReceiptOverview();

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidGrnOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForGrnOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === GoodsReceiptOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  if (!record) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">
            This goods receipt could not be loaded.
          </p>
          <Link
            to="/inventory-management/purchase-orders"
            className="btn btn-light btn-sm mt-2"
          >
            Back to purchase orders
          </Link>
        </Col>
      </Row>
    );
  }

  const displayTitle = record.code?.trim() || `GRN #${record.id}`;
  const codeLine = record.code ? `GRN #${record.id}` : null;
  const activeTab = grnOverviewActiveTabId(tabQuery);
  const po = record.purchaseOrder;

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{displayTitle}</h4>
                      {codeLine ? (
                        <p className="text-muted mb-1">
                          <span className="fw-medium">{codeLine}</span>
                        </p>
                      ) : null}
                      <div className="hstack gap-3 flex-wrap">
                        {record.receivedAt ? (
                          <div>
                            Received:{" "}
                            <span className="fw-medium">
                              <DateTime date={record.receivedAt} />
                            </span>
                          </div>
                        ) : null}
                        {record.stockLocation?.name ? (
                          <>
                            {record.receivedAt ? <div className="vr" /> : null}
                            <div>
                              Location:{" "}
                              <span className="fw-medium">
                                {record.stockLocation.name}
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto">
                    {po?.id ? (
                      <Link
                        to={`/inventory-management/purchase-orders/${po.id}`}
                        className="btn btn-primary btn-sm"
                      >
                        <i className="ri-file-list-3-line align-bottom me-1" />
                        View PO
                      </Link>
                    ) : null}
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "2" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("2");
                      }}
                      href="#"
                    >
                      Lines
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="2">
              <LinesTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>

      {po?.id ? (
        <Link
          to={`/inventory-management/purchase-orders/${po.id}`}
          className="btn btn-link ps-0 mt-3"
        >
          ← Back to purchase order
        </Link>
      ) : (
        <Link
          to="/inventory-management/purchase-orders"
          className="btn btn-link ps-0 mt-3"
        >
          ← Back to purchase orders
        </Link>
      )}
    </React.Fragment>
  );
};

export default Section;
